import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useCartContext } from '../../context/cartContext/useCartContext';
import { useLocaleContext } from '../../context/localeContext';
import { useFreeGiftModal } from '../../hooks/useFreeGiftModal';
import { Locale } from '../../utils/internationalization';
import { InformationPopUp } from '../popUps';
import { setFreeGiftPopupCookie, shouldShowFreeGiftPopup } from './freeGiftUtils';

export function FreeGiftConfirmationModalForPromoCode(): ReactElement {
  const { t } = useTranslation(['lib-global-common']);
  const { cart } = useCartContext();
  const discountItems = cart?.discountItems;
  const { isVisible, showModal, hideModal } = useFreeGiftModal();
  const { locale } = useLocaleContext();

  // Memoize eligibleGift logic to prevent cookie reads on every render
  const eligibleGift = useMemo(() => {
    // Early exit if no discount items
    if (!discountItems?.length) return null;

    return discountItems.find((item) => {
      if (!item?.lineItemId || item.isFreeGiftOutOfStock) return false;

      // Only read cookie when there is a valid item
      const shouldShowPopup = shouldShowFreeGiftPopup(item.lineItemId, locale || Locale.DEFAULT_LOCALE);
      return shouldShowPopup && item.isPromoCodeDiscount;
    });
  }, [discountItems, locale]);

  useEffect(() => {
    if (eligibleGift && eligibleGift.lineItemId) {
      showModal();
    }
  }, [eligibleGift, showModal]);

  const saveCookieForCurrentGift = useCallback(() => {
    if (eligibleGift?.lineItemId) {
      setFreeGiftPopupCookie(eligibleGift.lineItemId, locale || Locale.DEFAULT_LOCALE);
    }
  }, [eligibleGift, locale]);

  const handleClose = useCallback(() => {
    hideModal();
    saveCookieForCurrentGift();
  }, [saveCookieForCurrentGift, hideModal]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose],
  );

  return (
    <InformationPopUp
      isOpen={isVisible}
      message={t('free.gift.info.message')}
      description={t('free.gift.info.description')}
      confirmationLabel={t('free.gift.ok.button.label')}
      confirmationText={t('free.gift.ok.button')}
      handleCloseOverlay={handleClose}
      handleOpenChange={handleOpenChange}
      data-testid="freeGiftConfirmationModal"
    />
  );
}
